import React, { useState, useCallback } from 'react'
import {
  SettingsField,
  SettingsPopoverHeader,
  SettingsToggle,
} from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import { IconCategory } from '@deepguide-ai/types'
import { Separator } from '@deepguide-ai/ui'
import { Switch } from '@deepguide-ai/ui'
import { Button } from '@deepguide-ai/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@deepguide-ai/ui'
import { Input } from '@deepguide-ai/ui/input'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { ChevronDown, Plus, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconEditor } from '@/components/ui/icon-editor'
import { EmojiPicker } from '@/components/emoji-picker'
import { IconComponent } from '@/components/icon-component'
import { AccordionAttributes } from './accordion-extension'

interface AccordionNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function AccordionNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: AccordionNodeViewProps) {
  const attrs = node.attrs as AccordionAttributes

  const title = attrs.title || 'Accordion'
  const description = attrs.description || ''
  const defaultOpen = attrs.defaultOpen || false
  const icon = attrs.icon || 'sparkles'
  const iconType = attrs.iconType || 'REGULAR'
  const iconCategory = attrs.iconCategory || 'LUCIDE'
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false

  const [isExpanded, setIsExpanded] = useState(true) // Start expanded for new accordions
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Temporary state for settings
  const [tempTitle, setTempTitle] = useState(title)
  const [tempDescription, setTempDescription] = useState(description)
  const [tempDefaultOpen, setTempDefaultOpen] = useState(defaultOpen)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)

  // Handle toggle accordion
  const toggleAccordion = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsExpanded(!isExpanded)
  }

  // Handle add new accordion
  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = getPos()
    if (pos === undefined) return

    // Insert new accordion after current one
    const newAccordionPos = pos + node.nodeSize

    editor.commands.insertContentAt(newAccordionPos, {
      type: 'Accordion',
      attrs: {
        title: 'Accordion Title',
        defaultOpen: true,
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  }

  // Handle delete accordion
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    deleteNode()
  }

  // Handle save settings
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<AccordionAttributes> = {}

    if (tempTitle.trim() !== title) {
      newAttributes.title = tempTitle.trim() || 'Accordion'
    }

    if (tempDescription !== description) {
      newAttributes.description = tempDescription
    }

    if (tempDefaultOpen !== defaultOpen) {
      newAttributes.defaultOpen = tempDefaultOpen
    }

    if (tempIcon !== icon) {
      newAttributes.icon = tempIcon
    }

    if (tempIconType !== iconType) {
      newAttributes.iconType = tempIconType
    }

    if (tempIconColor !== iconColor) {
      newAttributes.iconColor = tempIconColor
    }

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
    }

    if (tempIconCategory !== iconCategory) {
      newAttributes.iconCategory = tempIconCategory
    }

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }

    setIsPopoverOpen(false)
  }, [
    tempTitle,
    title,
    tempDescription,
    description,
    tempDefaultOpen,
    defaultOpen,
    tempIcon,
    iconCategory,
    tempIconCategory,
    icon,
    tempIconType,
    iconType,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    updateAttributes,
  ])

  // Handle cancel settings
  const cancelSettings = useCallback(() => {
    setTempTitle(title)
    setTempDescription(description)
    setTempDefaultOpen(defaultOpen)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempIconCategory(iconCategory)
    setIsPopoverOpen(false)
  }, [
    title,
    description,
    defaultOpen,
    icon,
    iconType,
    iconColor,
    showIcon,
    iconCategory,
  ])

  // Handle open settings
  const openSettings = useCallback(() => {
    setTempTitle(title)
    setTempDescription(description)
    setTempDefaultOpen(defaultOpen)
    setTempIconCategory(iconCategory)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setIsPopoverOpen(true)
  }, [
    title,
    description,
    defaultOpen,
    icon,
    iconType,
    iconColor,
    showIcon,
    iconCategory,
  ])

  return (
    <NodeViewWrapper
      className={cn(
        'accordion-item group/item border-border relative my-5 overflow-hidden rounded-md border border-solid p-0',
        selected && 'ProseMirror-selectednode'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className='accordion-header bg-accent flex cursor-pointer items-center justify-between gap-2 px-5 py-4'
        onClick={toggleAccordion}
        contentEditable={false}
      >
        <div className='flex flex-1 items-start gap-1.5 leading-[28px] font-semibold'>
          <div className='flex-shrink-0 pt-1.5'>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isExpanded ? 'rotate-180 transform' : ''
              )}
            />
          </div>

          <div
            className={`flex-shrink-0 ${iconCategory === 'EMOJI' ? 'h-[22px] pt-[1.5px]' : 'pt-1'}`}
          >
            {showIcon && (
              <IconComponent
                iconName={icon}
                iconCategory={iconCategory}
                iconType={iconType}
                iconColor={iconColor}
                size={20}
                className={`transition-colors ${
                  iconCategory === 'EMOJI'
                    ? 'h-[20px] w-[20px] !text-[20px]'
                    : ''
                }`}
              />
            )}
          </div>

          <div className='flex-1'>
            {title}
            {description && (
              <div className='text-muted-foreground mt-1 text-sm font-normal'>
                {description}
              </div>
            )}
          </div>
        </div>

        {editor.isEditable && (
          <div
            className={`flex items-center gap-0 opacity-0 group-hover/item:opacity-100`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant='ghost' size='menuAction' onClick={handleAdd}>
              <Plus className='text-foreground h-4 w-4' />
            </Button>

            {(isHovered || isPopoverOpen) && (
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant='ghost'
                    size='menuAction'
                    onClick={(e) => {
                      e.stopPropagation()
                      openSettings()
                    }}
                  >
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>

                <PopoverContent
                  className='w-80 p-4 pb-3'
                  align='end'
                  side='bottom'
                  sideOffset={8}
                >
                  <div className='space-y-4'>
                    <SettingsPopoverHeader
                      title='Accordion Settings'
                      onClose={cancelSettings}
                    />

                    <SettingsField label='Title' className='mb-5'>
                      <div className='flex items-center gap-2'>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                        <Input
                          type='text'
                          value={tempTitle}
                          onChange={(e) => setTempTitle(e.target.value)}
                          placeholder='Enter title...'
                          className='flex-1'
                        />
                      </div>
                    </SettingsField>

                    <SettingsField label='Description'>
                      <Textarea
                        value={tempDescription}
                        onChange={(e) => setTempDescription(e.target.value)}
                        placeholder='Enter description...'
                        rows={3}
                        className='resize-none'
                      />
                    </SettingsField>

                    <Separator />

                    {/* <div>
                    <div className='flex justify-between items-center mb-4 w-full'>
                      <label className='text-xs leading-4 text-muted-foreground'>
                        Show Icon
                      </label>
                      <Switch
                        checked={tempShowIcon}
                        onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                      />
                    </div>

                    {tempShowIcon && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <IconEditor
                          selectedIcon={tempIcon}
                          iconType={tempIconType}
                          iconColor={tempIconColor}
                          onIconChange={setTempIcon}
                          onTypeChange={setTempIconType}
                          onColorChange={setTempIconColor}
                        />
                      </div>
                    )}
                  </div> */}
                    {/* <div className='flex flex-col gap-2'>
                      <label className='text-muted-foreground text-xs leading-4'>
                        Icon
                      </label>
                      <EmojiPicker
                        selectedIcon={tempShowIcon ? tempIcon : undefined}
                        iconColor={tempIconColor}
                        iconCategory={tempIconCategory}
                        onEmojiSelect={(emoji) => {
                          setTempShowIcon(true)
                          setTempIcon(emoji)
                          setTempIconCategory('EMOJI')
                        }}
                        onIconChange={(
                          iconName: string,
                          iconCategory: IconCategory
                        ) => {
                          if (iconName === '') {
                            setTempIconCategory('LUCIDE')
                            setTempShowIcon(false)
                            return
                          }
                          setTempShowIcon(true)
                          setTempIcon(iconName)
                          setTempIconCategory(iconCategory)
                        }}
                        onColorChange={setTempIconColor}
                        isPopoverMode={true}
                      />
                    </div> */}

                    <SettingsToggle label='Open by Default' className='mt-5'>
                      <Switch
                        checked={tempDefaultOpen}
                        onCheckedChange={() =>
                          setTempDefaultOpen(!tempDefaultOpen)
                        }
                      />
                    </SettingsToggle>

                    <Separator className='!mt-5 !mb-3 -ml-4 !w-[calc(100%+32px)]' />

                    <div className='flex items-center justify-between'>
                      <Button
                        variant='destructive'
                        className='h-8 !bg-transparent px-2 py-1.5 text-[var(--destructive)] shadow-none'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(e)
                        }}
                      >
                        Delete
                      </Button>
                      <div className='flex items-center space-x-2'>
                        <Button
                          variant='outline'
                          className='h-8 border-[var(--border)] px-2 py-1.5'
                          onClick={cancelSettings}
                        >
                          Cancel
                        </Button>
                        <Button
                          className='h-8 px-2 py-1.5'
                          onClick={saveSettings}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          'accordion-content overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out [&_.slate-p]:!my-0',
          isExpanded
            ? 'max-h-[500px] opacity-100'
            : 'max-h-0 border-t-0 opacity-0'
        )}
      >
        <div className='px-5 py-5'>
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  )
}
